import React, { useEffect, useRef, useState } from "react";
import Modal from "../modal/modal";
import { useApplyEventPromoCodeMutation, useCreateEventOrderMutation } from "../../services/store/api/eventsApi";
import style from "./event-card.module.css";
import { TEvent, TEventTariffOption } from "../../types";
import AppHelper from '../../utils/helper';
import { useAppSelector } from "../../services/store/hooks";
import NeedProfile from "../need-profile/need-profile";
import ProfileModeration from "../profile-moderation/profile-moderation";

declare global {
    interface Window {
        YooMoneyCheckoutWidget?: any;
    }
}

interface Props {
    event: TEvent;
    isOpen: boolean;
    onClose: () => void;
}

const formatPhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let normalized = digits;

    if (normalized.startsWith('8')) {
        normalized = normalized.slice(1);
    }
    if (normalized.startsWith('7')) {
        normalized = normalized.slice(1);
    }

    normalized = normalized.slice(0, 10);

    let result = '+7';

    if (normalized.length > 0) {
        result += ' ' + normalized.slice(0, 3);
    }
    if (normalized.length >= 4) {
        result += ' ' + normalized.slice(3, 6);
    }
    if (normalized.length >= 7) {
        result += '-' + normalized.slice(6, 8);
    }
    if (normalized.length >= 9) {
        result += '-' + normalized.slice(8, 10);
    }

    return result.trim();
};

const normalizePhone = (value: string) => {
    let digits = value.replace(/\D/g, '');
    if (digits.startsWith('8')) {
        digits = '7' + digits.slice(1);
    } else if (!digits.startsWith('7')) {
        digits = '7' + digits;
    }
    digits = digits.slice(0, 11);
    return `+${digits}`;
};

const EventOrderModal = ({ event, isOpen, onClose }: Props) => {

    const [phone, setPhone] = useState("+7");
    const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
    const [freeOrderSuccess, setFreeOrderSuccess] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [isPromoOpen, setIsPromoOpen] = useState(false);
    const [promoInfo, setPromoInfo] = useState<{
        promo_code: string;
        amount: number;
        original_amount: number;
        discount_amount: number;
    } | null>(null);
    const [promoError, setPromoError] = useState("");
    const [selectedTariffOptionId, setSelectedTariffOptionId] = useState<number | null>(null);
    const widgetRef = useRef<any>(null);
    const [phoneError, setPhoneError] = useState("");
    const { profile } = useAppSelector(store => store.user);

    const [createOrder, { isLoading, error }] = useCreateEventOrderMutation();
    const [applyPromoCode, { isLoading: isPromoLoading }] = useApplyEventPromoCodeMutation();

    const tariffOptions = event.tariff_options ?? [];
    const hasTariffOptions = tariffOptions.length > 0;
    const isBaseTariffSelected = selectedTariffOptionId === null;
    const selectedTariffOption = tariffOptions.find((option) => option.id === selectedTariffOptionId) ?? null;
    const selectedTariffModifier = selectedTariffOption?.price_modifier ?? 0;
    const basePrice = event.price ?? 0;
    const paymentAmount = promoInfo?.amount ?? basePrice + selectedTariffModifier;
    const discountAmount = promoInfo?.discount_amount ?? 0;

    const getApiErrorMessage = (apiError: unknown) => {
        const data = (apiError as any)?.data;
        const firstError = data?.errors ? Object.values(data.errors)[0] : null;
        if (Array.isArray(firstError) && firstError[0]) {
            return firstError[0];
        }

        return data?.message ?? "Промокод не применен";
    };

    const handleApplyPromoCode = async () => {
        const normalizedPromoCode = promoCode.trim().toUpperCase();
        setPromoError("");

        if (!normalizedPromoCode) {
            setPromoInfo(null);
            return;
        }

        try {
            const result = await applyPromoCode({
                eventId: event.id,
                promo_code: normalizedPromoCode,
                event_tariff_option_id: selectedTariffOptionId ?? undefined,
            }).unwrap();
            setPromoCode(result.promo_code);
            setPromoInfo(result);
        } catch (apiError) {
            setPromoInfo(null);
            setPromoError(getApiErrorMessage(apiError));
        }
    };

    const handleSubmitOrder = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const normalizedPhone = normalizePhone(phone);
        const isValidPhone = /^\+7\d{10}$/.test(normalizedPhone);
        if (!isValidPhone) {
            setPhoneError("Введите российский номер в формате +7XXXXXXXXXX");
            return;
        }

        try {
            const result = await createOrder({
                eventId: event.id,
                phone: normalizedPhone,
                promo_code: promoCode.trim() || undefined,
                event_tariff_option_id: selectedTariffOptionId ?? undefined,
            }).unwrap();

            if (result.is_free) {
                setFreeOrderSuccess(true);
                return;
            }

            setConfirmationToken(result.confirmation_token);
        } catch {
            // handled via error state
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setConfirmationToken(null);
            setFreeOrderSuccess(false);
            setPromoCode("");
            setIsPromoOpen(false);
            setPromoInfo(null);
            setPromoError("");
            setSelectedTariffOptionId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !confirmationToken) return;

        const initWidget = () => {
            if (!window.YooMoneyCheckoutWidget) return;
            if (widgetRef.current?.destroy) {
                widgetRef.current.destroy();
            }
            widgetRef.current = new window.YooMoneyCheckoutWidget({
                confirmation_token: confirmationToken,
                return_url: window.location.href,
                error_callback: () => {
                    // ignore for now
                },
            });
            widgetRef.current.render("yookassa-widget");
        };

        const scriptId = "yookassa-checkout-widget";
        const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (window.YooMoneyCheckoutWidget) {
            initWidget();
            return;
        }

        if (!existing) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://yookassa.ru/checkout-widget/v1/checkout-widget.js";
            script.async = true;
            script.onload = initWidget;
            document.body.appendChild(script);
        } else {
            existing.addEventListener("load", initWidget);
        }

        return () => {
            if (widgetRef.current?.destroy) {
                widgetRef.current.destroy();
            }
        };
    }, [confirmationToken, isOpen]);

    const startDateTime = AppHelper.formatDateTimeRuLong(
        event.date_start,
        event.time_start,
    );

    if (!isOpen) return null;

    // Проверка наличия профиля
    if (!profile) {
        return (
            <NeedProfile onClose={onClose} title="Запись на событие" />
        );
    }

    if (profile.status !== 'ACTIVE') {
        return (
            <Modal header="Запись на событие" onClose={onClose}>
                <ProfileModeration />
            </Modal>
        );
    }

    return (
        <Modal header="Запись на событие" onClose={onClose}>
            {!confirmationToken && !freeOrderSuccess && (
                <div className={style.orderModalBody}>
                    <div className={style.orderEventBox}>
                        <div className={style.orderEventLabel}>Мероприятие</div>
                        <div className={style.orderEventTitle}>{event.title}</div>
                        <div className={style.orderEventMeta}>{startDateTime}</div>
                        <div className={style.orderEventMeta}>
                            {event.location}{event.metro_station ? `, м. ${event.metro_station}` : ""}
                        </div>
                        </div>

                    <form onSubmit={handleSubmitOrder} className={style.orderForm}>
                        {hasTariffOptions && (
                            <div className={style.tariffBox}>
                                <div className={style.orderLabel}>Тариф</div>
                                <div className={style.tariffOptions}>
                                    <label
                                        className={`${style.tariffOption} ${isBaseTariffSelected ? style.tariffOptionSelected : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="event_tariff_option_id"
                                            value=""
                                            checked={isBaseTariffSelected}
                                            onChange={() => {
                                                setSelectedTariffOptionId(null);
                                                setPromoInfo(null);
                                                setPromoError("");
                                            }}
                                        />
                                        <span className={style.tariffOptionContent}>
                                            <span className={style.tariffOptionTitle}>Базовый тариф</span>
                                            <span className={style.tariffOptionDescription}>Только участие в мероприятии</span>
                                        </span>
                                        <strong className={style.tariffOptionPrice}>+0 ₽</strong>
                                    </label>
                                    {tariffOptions.map((option: TEventTariffOption) => (
                                        <label
                                            className={`${style.tariffOption} ${selectedTariffOptionId === option.id ? style.tariffOptionSelected : ""}`}
                                            key={option.id}
                                        >
                                            <input
                                                type="radio"
                                                name="event_tariff_option_id"
                                                value={option.id}
                                                checked={selectedTariffOptionId === option.id}
                                                onChange={() => {
                                                    setSelectedTariffOptionId(option.id);
                                                    setPromoInfo(null);
                                                    setPromoError("");
                                                }}
                                            />
                                            <span className={style.tariffOptionContent}>
                                                <span className={style.tariffOptionTitle}>{option.title}</span>
                                                {option.description && (
                                                    <span className={style.tariffOptionDescription}>{option.description}</span>
                                                )}
                                            </span>
                                            <strong className={style.tariffOptionPrice}>
                                                +{AppHelper.formatPrice(option.price_modifier)} ₽
                                            </strong>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={style.orderRow}>
                            <label htmlFor="order-phone" className={style.orderLabel}>Ваш номер телефона</label>
                            <input
                                id="order-phone"
                                type="tel"
                                required
                                value={phone}
                           
                                onChange={(e) => {
                                    setPhone(formatPhoneInput(e.target.value));
                                    setPhoneError("");
                                }}
                                className={style.orderInput}
                                placeholder="+7 (___) ___-__-__"
                            />
                        </div>

                        <div className={style.priceBreakdown}>
                            <div className={style.priceBreakdownRow}>
                                <span>Базовая стоимость мероприятия</span>
                                <strong>{AppHelper.formatPrice(basePrice)} ₽</strong>
                            </div>
                            {selectedTariffModifier > 0 && (
                                <div className={style.priceBreakdownRow}>
                                    <span>Вариант тарифа</span>
                                    <strong>+{AppHelper.formatPrice(selectedTariffModifier)} ₽</strong>
                                </div>
                            )}
                            {discountAmount > 0 && (
                                <div className={`${style.priceBreakdownRow} ${style.priceBreakdownDiscount}`}>
                                    <span>Скидка по промокоду</span>
                                    <strong>-{AppHelper.formatPrice(discountAmount)} ₽</strong>
                                </div>
                            )}
                            <div className={style.orderTotalBox}>
                                <span>К оплате</span>
                                <strong>{AppHelper.formatPrice(paymentAmount)} ₽</strong>
                            </div>
                        </div>

                        <div className={`${style.promoBox} ${isPromoOpen ? style.promoBoxOpen : ""}`}>
                            <button
                                type="button"
                                className={style.promoToggle}
                                onClick={() => setIsPromoOpen((value) => !value)}
                                aria-expanded={isPromoOpen}
                            >
                                <span className={style.promoToggleIcon} aria-hidden="true">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                        <path d="M20.59 13.41L13.42 20.58C13.05 20.95 12.54 21.16 12.01 21.16C11.48 21.16 10.97 20.95 10.6 20.58L3 12.99V4H11.99L20.59 12.59C20.98 12.98 20.98 13.02 20.59 13.41Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M7.5 7.5H7.51" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
                                        <path d="M10 15L15 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        <path d="M10.5 10.5H10.51" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                        <path d="M14.5 14.5H14.51" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <span className={style.promoToggleText}>Есть промокод?</span>
                                {promoInfo && <span className={style.promoToggleBadge}>применен</span>}
                                <span className={style.promoToggleArrow} aria-hidden="true">
                                    <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                                        <path d="M1 1L7 7L13 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>

                            {isPromoOpen && (
                                <div className={style.promoPanel}>
                                    <div className={style.promoControls}>
                                        <input
                                            id="order-promo"
                                            type="text"
                                            value={promoCode}
                                            onChange={(e) => {
                                                setPromoCode(e.target.value.toUpperCase());
                                                setPromoInfo(null);
                                                setPromoError("");
                                            }}
                                            className={style.orderInput}
                                            placeholder="Введите промокод"
                                        />
                                        <button
                                            type="button"
                                            className={style.promoButton}
                                            disabled={isPromoLoading}
                                            onClick={handleApplyPromoCode}
                                        >
                                            {isPromoLoading ? "Проверяем" : "Применить"}
                                        </button>
                                    </div>
                                    {promoInfo && (
                                        <div className={style.promoSuccess}>
                                            Промокод применен
                                        </div>
                                    )}
                                    {promoError && (
                                        <div className={style.promoError}>{promoError}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {phoneError && (
                            <div className="text-red-500 text-xs mb-2">{phoneError}</div>
                        )}

                        {error && (
                            <div className="text-red-500 text-sm">
                                Ошибка создания заказа. Попробуйте снова.
                            </div>
                        )}

                        <div className={style.orderActions}>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn-link"
                            >
                                {isLoading ? "Создание..." : "Перейти к оплате"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-link btn-link-cancel"
                            >
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {freeOrderSuccess && (
                <div className={style.orderSuccess}>
                    <div className={style.orderSuccessTitle}>Вы успешно записаны!</div>
                    <div>Промокод полностью покрыл стоимость мероприятия.</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-link"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {confirmationToken && (
                <div className={style.orderWidget}>
                    <div id="yookassa-widget" />
                </div>
            )}
        </Modal>
    );
};

export default EventOrderModal;
